// web/src/routes/supplements/SupplementEdit.jsx
// Setting a product up, which is the only time anybody should have to think.
//
// Taylor's one condition on the whole module was that she can change horses,
// doses, vendors and container sizes herself without asking anybody. So this
// is the screen that has to be right: everything downstream is arithmetic over
// what gets typed here, and a wrong number here is invisible for a month and
// then the barn runs out of Isoxsuprine.
//
// THE ECHO
//
// Two numbers decide every prediction — how many doses are in a container, and
// how many a horse gets a day — and the only way to get them wrong is to count
// them in different units. Taylor's own spreadsheet does exactly that:
// Isoxsuprine's "60" counts scoops while Emcelle's "~120" counts days, so read
// the same way one of them is out by a factor of two.
//
// No amount of labelling fixes that, because both labels look right. What
// fixes it is showing the consequence while the number is being typed — "that
// is 30 days for one horse" — because Taylor knows how long a jar lasts and
// will see 120 days on something that lasts two months. Same trick as the time
// field echoing "1:00 PM": a guess you can see is a guess you can correct.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { friendly, trim } from './SupplementDetail';

// A value no vendor can have, so picking it is unambiguous.
const NEW_VENDOR = '__new__';

const KINDS = [
  { value: 'supplement', label: 'Supplement' },
  { value: 'medication', label: 'Medication (Rx)' },
];

export default function SupplementEdit() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState(null);
  const [addingVendor, setAddingVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  const { data: vendorData } = useQuery({
    queryKey: ['supplement-vendors'],
    queryFn: () => api.get('/supplements/vendors'),
  });
  const { data, isLoading } = useQuery({
    queryKey: ['supplement', id],
    queryFn: () => api.get(`/supplements/${id}`),
    enabled: editing,
  });

  const s = data?.supplement;
  const [form, setForm] = useState(null);
  // Seed the form once the product arrives. Doing this in render rather than an
  // effect keeps the first paint correct; an effect would show empty fields for
  // a frame and steal whatever had already been typed into them.
  if (editing && s && form === null) {
    setForm({
      name: s.name,
      kind: s.kind,
      vendorId: s.vendorId ?? '',
      doseUnit: s.doseUnit,
      containerLabel: s.containerLabel ?? '',
      dosesPerContainer: s.dosesPerContainer == null ? '' : String(s.dosesPerContainer),
      defaultDailyDoses: s.defaultDailyDoses == null ? '' : String(s.defaultDailyDoses),
      leadTimeDays: s.leadTimeDays == null ? '' : String(s.leadTimeDays),
      safetyDays: String(s.safetyDays),
      notes: s.notes ?? '',
      yieldVerified: s.yieldVerified,
    });
  }
  const f = form ?? blank();
  const set = (k) => (e) => setForm({ ...f, [k]: e.target.value });

  // Taylor's ask, and the right shape for it: a vendor is two facts — a name
  // and how long they take — so making her leave a half-filled supplement to
  // go and create one somewhere else is three navigations to type eight
  // characters. The new vendor is selected the moment it exists.
  const addVendor = useMutation({
    mutationFn: (body) => api.post('/supplements/vendors', body),
    onMutate: () => setError(null),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['supplement-vendors'] });
      setForm((cur) => ({ ...(cur ?? blank()), vendorId: r.id }));
      setAddingVendor(false);
    },
    onError: (e) => setError(friendly(e)),
  });

  const editVendor = useMutation({
    mutationFn: ({ id: vid, body }) => api.patch(`/supplements/vendors/${vid}`, body),
    onMutate: () => setError(null),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['supplement-vendors'] });
      // Every product carrying this vendor shows its name and lead time, so
      // the pages that quote it have to be told it moved.
      qc.invalidateQueries({ queryKey: ['supplements'] });
      qc.invalidateQueries({ queryKey: ['supplement-orders'] });
      setEditingVendor(null);
    },
    onError: (e) => setError(friendly(e)),
  });

  const save = useMutation({
    mutationFn: (body) => (editing ? api.patch(`/supplements/${id}`, body) : api.post('/supplements', body)),
    onMutate: () => setError(null),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['supplements'] });
      qc.invalidateQueries({ queryKey: ['supplement', id] });
      nav(`/supplements/${editing ? id : r.id}`);
    },
    onError: (e) => setError(friendly(e)),
  });

  const perContainer = num(f.dosesPerContainer);
  const perDay = num(f.defaultDailyDoses);
  const unit = (f.doseUnit || 'dose').trim();

  function submit(e) {
    e.preventDefault();
    if (!f.name.trim()) return;
    save.mutate({
      name: f.name.trim(),
      kind: f.kind,
      vendorId: f.vendorId || null,
      doseUnit: unit,
      containerLabel: f.containerLabel.trim() || null,
      // Empty is UNKNOWN and is sent as null, never as zero. The whole module
      // leans on that difference: a product nobody has measured must predict
      // nothing rather than predict badly.
      dosesPerContainer: f.dosesPerContainer === '' ? null : perContainer,
      defaultDailyDoses: f.defaultDailyDoses === '' ? null : perDay,
      leadTimeDays: f.leadTimeDays === '' ? null : Number(f.leadTimeDays),
      safetyDays: Number(f.safetyDays || 7),
      notes: f.notes.trim() || null,
      yieldVerified: !!f.yieldVerified,
    });
  }

  if (editing && isLoading) {
    return <div className="p-4 md:p-6 max-w-2xl mx-auto"><div className="h-64 card animate-pulse" /></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      <header className="mb-4 pb-2 border-b-2 border-hunter/30">
        <Link to={editing ? `/supplements/${id}` : '/supplements'} className="text-xs text-hunter hover:underline">
          ← {editing ? s?.name : 'Supplements'}
        </Link>
        <h1 className="text-lg md:text-xl font-semibold leading-tight">
          {editing ? 'Edit supplement' : 'Add a supplement'}
        </h1>
      </header>

      <form onSubmit={submit} className="space-y-5">
        <div className="card p-4 space-y-4">
          <Label text="Name">
            <input className="input" value={f.name} onChange={set('name')} autoFocus={!editing} />
          </Label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Label text="Kind">
              <select className="input" value={f.kind} onChange={set('kind')}>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </Label>
            <Label text="Vendor">
              <select
                className="input"
                value={f.vendorId}
                onChange={(e) => {
                  // The last option is a door, not a choice. Picking it must
                  // not leave the field reading "Add a vendor" if the form is
                  // then cancelled, so the value is left exactly as it was.
                  if (e.target.value === NEW_VENDOR) setAddingVendor(true);
                  else setForm({ ...f, vendorId: e.target.value });
                }}
              >
                <option value="">Not set</option>
                {(vendorData?.vendors ?? []).filter((v) => v.active).map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
                <option value={NEW_VENDOR}>+ Add a vendor…</option>
              </select>
              {/* A vendor is somebody's name and how long they take, and both
                  get typed wrong. Editing was reachable from nowhere — the
                  endpoint existed and no screen asked for it. */}
              {f.vendorId && !addingVendor && (
                <button
                  type="button"
                  className="mt-1 text-xs text-hunter hover:underline"
                  onClick={() => setEditingVendor(f.vendorId)}
                >
                  Edit {(vendorData?.vendors ?? []).find((v) => v.id === f.vendorId)?.name ?? 'this vendor'}
                </button>
              )}
            </Label>
          </div>
        </div>

        {editingVendor && (
          <NewVendor
            key={editingVendor}
            initial={(vendorData?.vendors ?? []).find((v) => v.id === editingVendor) ?? null}
            busy={editVendor.isPending}
            onCancel={() => setEditingVendor(null)}
            onSave={(body) => editVendor.mutate({ id: editingVendor, body })}
          />
        )}

        {addingVendor && (
          <NewVendor
            busy={addVendor.isPending}
            onCancel={() => setAddingVendor(false)}
            onSave={(body) => addVendor.mutate(body)}
          />
        )}

        <div className="card p-4 space-y-4">
          <p className="text-xs font-medium uppercase tracking-wider text-ink-400">How it is measured</p>

          <Label
            text="What one dose is called"
            hint="scoop, tab, pump, tube — whatever the barn says when it hands it out"
          >
            <input className="input" value={f.doseUnit} onChange={set('doseUnit')} placeholder="scoop" />
          </Label>

          <Label text="What the container looks like" hint="Just for reading — no sums use this">
            <input className="input" value={f.containerLabel} onChange={set('containerLabel')} placeholder="11lb bag" />
          </Label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Label text={`${cap(unit)}s in a container`} hint="Leave blank if nobody has counted">
              <input className="input" inputMode="decimal" value={f.dosesPerContainer}
                onChange={set('dosesPerContainer')} placeholder="60" />
            </Label>
            <Label
              text={`${cap(unit)}s a horse gets a day`}
              hint={`The usual amount. A horse that needs a different one \u2014 2 ${unit}s instead of 1 \u2014 gets it set under "Who gets it".`}
            >
              <input className="input" inputMode="decimal" value={f.defaultDailyDoses}
                onChange={set('defaultDailyDoses')} placeholder="2" />
            </Label>
          </div>

          {/* The echo. The single most load-bearing sentence on the page. */}
          <Echo perContainer={perContainer} perDay={perDay} unit={unit} />

          {f.dosesPerContainer !== '' && (
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5" checked={!!f.yieldVerified}
                onChange={(e) => setForm({ ...f, yieldVerified: e.target.checked })} />
              <span>
                <span className="font-medium">Somebody actually counted this</span>
                <span className="block text-xs text-ink-400">
                  Leave it off for a number carried over from the spreadsheet. Both work; only a
                  counted one is shown without a caveat.
                </span>
              </span>
            </label>
          )}
        </div>

        <div className="card p-4 space-y-4">
          <p className="text-xs font-medium uppercase tracking-wider text-ink-400">When to reorder</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Label text="Lead time, days" hint="Blank uses the vendor's">
              <input className="input" inputMode="numeric" value={f.leadTimeDays}
                onChange={set('leadTimeDays')} placeholder={String(vendorLead(vendorData, f.vendorId) ?? 7)} />
            </Label>
            <Label text="Safety buffer, days" hint="Covers the open tub nobody counts">
              <input className="input" inputMode="numeric" value={f.safetyDays} onChange={set('safetyDays')} />
            </Label>
          </div>
          <p className="text-xs text-ink-500">
            A shelf is flagged{' '}
            <strong>{(Number(f.leadTimeDays) || vendorLead(vendorData, f.vendorId) || 7) + (Number(f.safetyDays) || 0)} days</strong>{' '}
            before it is due to run out.
          </p>
        </div>

        <div className="card p-4">
          <Label text="Notes">
            <textarea className="input" rows={2} value={f.notes} onChange={set('notes')} />
          </Label>
        </div>

        {error && <p className="text-sm text-rose-700">{error}</p>}

        <div className="flex justify-end gap-2">
          <Link to={editing ? `/supplements/${id}` : '/supplements'} className="btn-secondary">Cancel</Link>
          <button type="submit" className="btn-primary" disabled={save.isPending || !f.name.trim()}>
            {save.isPending ? 'Saving…' : editing ? 'Save' : 'Add it'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * What the two numbers mean, in days, while they are being typed.
 *
 * Says nothing when it cannot: half a pair is not an error, it is a form
 * somebody is halfway through, and a red warning on every keystroke trains
 * people to ignore the one that matters.
 */
function Echo({ perContainer, perDay, unit }) {
  if (perContainer == null && perDay == null) {
    return (
      <p className="text-sm text-ink-400">
        Fill both in and this will say how long a container lasts.
      </p>
    );
  }
  if (perContainer == null || perDay == null) {
    return (
      <p className="text-sm text-ink-400">
        {perContainer == null
          ? `No container size yet, so nothing can be predicted for this product.`
          : `No daily dose yet — a horse without one of its own can't be counted.`}
      </p>
    );
  }
  const days = perContainer / perDay;
  return (
    <p className="rounded-md bg-hunter/10 border border-hunter/25 px-3 py-2 text-sm text-hunter">
      One container is <strong>{trim(days)} days</strong> for one horse
      <span className="text-hunter/70">
        {' '}({trim(perContainer)} {unit}{perContainer === 1 ? '' : 's'} at {trim(perDay)} a day)
      </span>
      .
      <span className="block text-xs text-hunter/70 mt-0.5">
        If that is not how long a container actually lasts, one of the two numbers is counted in
        the wrong unit.
      </span>
    </p>
  );
}

/**
 * A vendor, created without leaving the supplement you were halfway through.
 *
 * Deliberately not a modal. A dialog over a form you are filling in hides the
 * thing you opened it from, and this one is two fields — it belongs inline,
 * where the name you type is next to the product it is for.
 *
 * Enter does not submit the outer form: this is nested inside it, so the
 * button is a plain button and the keystroke is handled here.
 */
function NewVendor({ initial = null, busy, onCancel, onSave }) {
  const editing = !!initial;
  const [name, setName] = useState(initial?.name ?? '');
  const [lead, setLead] = useState(String(initial?.leadTimeDays ?? 7));

  const submit = () => {
    const n = name.trim();
    if (!n || busy) return;
    const d = Number(lead);
    onSave({ name: n, leadTimeDays: Number.isFinite(d) && d >= 0 ? Math.round(d) : 7 });
  };

  return (
    <div className="card p-4 space-y-3 border-hunter/40 bg-hunter/5">
      <p className="text-xs font-medium uppercase tracking-wider text-hunter">
        {editing ? 'Edit vendor' : 'New vendor'}
      </p>
      {editing && initial?.inUse > 1 && (
        // Changing a name changes it on every product that quotes it, which is
        // the point and is still worth saying before it happens.
        <p className="text-xs text-ink-500">
          Used by {initial.inUse} products — they will all show the new name.
        </p>
      )}
      <Label text="Name" hint="Who you actually order from — a shop, a vet's office, a rep">
        <input
          className="input"
          value={name}
          autoFocus
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          placeholder="Vet office"
        />
      </Label>
      <Label text="Lead time, days" hint="How long between ordering and it turning up">
        <input
          className="input w-24"
          inputMode="numeric"
          value={lead}
          disabled={busy}
          onChange={(e) => setLead(e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        />
      </Label>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={submit} disabled={busy || !name.trim()}>
          {busy ? 'Saving…' : editing ? 'Save vendor' : 'Add vendor'}
        </button>
      </div>
    </div>
  );
}

function Label({ text, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-700 mb-1">{text}</span>
      {children}
      {hint && <span className="block text-xs text-ink-400 mt-1">{hint}</span>}
    </label>
  );
}

function blank() {
  return {
    name: '', kind: 'supplement', vendorId: '', doseUnit: 'scoop', containerLabel: '',
    dosesPerContainer: '', defaultDailyDoses: '', leadTimeDays: '', safetyDays: '7',
    notes: '', yieldVerified: false,
  };
}

/** A positive number, or null for blank and for anything that isn't one. */
function num(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function cap(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function vendorLead(vendorData, vendorId) {
  return (vendorData?.vendors ?? []).find((v) => v.id === vendorId)?.leadTimeDays ?? null;
}
